import type { AgentTool } from "@elyracode/agent-core";
import { Text } from "@elyracode/tui";
import { type Static, Type } from "typebox";
import { type DecisionStoreOperations, findSimilarDecision, listDecisions, writeDecision } from "../decisions.js";
import type { ExtensionContext, ToolDefinition } from "../extensions/types.js";
import { wrapToolDefinition } from "./tool-definition-wrapper.js";

const decisionRecordSchema = Type.Object({
	title: Type.String({
		description:
			"Short imperative title naming the choice, e.g. 'Use Inertia over Livewire for the admin UI'. Becomes part of the decision id.",
	}),
	context: Type.String({
		description: "Why a decision was needed: the problem, constraints, and forces at play.",
	}),
	decision: Type.String({
		description: "What was chosen. State the choice plainly in one or two paragraphs.",
	}),
	alternatives: Type.Optional(
		Type.String({
			description: "Alternatives that were considered and why each was rejected.",
		}),
	),
	consequences: Type.Optional(
		Type.String({
			description: "Consequences and constraints this places on future work.",
		}),
	),
	supersedes: Type.Optional(
		Type.String({
			description:
				"Id of an earlier decision this one replaces (e.g. '2025-03-01-use-livewire-for-admin-ui'). The old decision is kept and marked superseded.",
		}),
	),
});

export type DecisionRecordToolInput = Static<typeof decisionRecordSchema>;

export type DecisionLogMode = "off" | "confirm" | "auto";

export interface DecisionRecordToolDetails {
	id: string | null;
	path: string | null;
	saved: boolean;
	rejected?: boolean;
	superseded?: string;
	disabled?: boolean;
}

export interface DecisionRecordToolOptions {
	/** Project root; decisions are stored under <cwd>/.elyra/memory/decisions. */
	cwd: string;
	/** Decision log mode. Read fresh on each call so setting changes take effect immediately. */
	getDecisionLog: () => DecisionLogMode;
	/** Current session id, recorded in the decision frontmatter when available. */
	getSessionId?: () => string | undefined;
	/** Custom operations (default: local filesystem). */
	operations?: DecisionStoreOperations;
}

function firstLine(text: string, max: number): string {
	const line =
		text
			.split(/\r?\n/)
			.find((l) => l.trim().length > 0)
			?.trim() ?? "";
	return line.length > max ? `${line.slice(0, max - 1)}\u2026` : line;
}

/**
 * Ask the user to approve recording a decision.
 * Returns true to record, false to discard. Falls back to true when no UI is present.
 */
async function confirmRecord(ctx: ExtensionContext | undefined, title: string, decision: string): Promise<boolean> {
	if (!ctx?.hasUI) return true;

	const result = await ctx.ui.custom<boolean>((tui, theme, _kb, done) => {
		let index = 0; // 0 = Record, 1 = Discard
		const options = ["Record decision", "Discard"];
		let cachedLines: string[] | undefined;

		function refresh() {
			cachedLines = undefined;
			tui.requestRender();
		}

		function handleInput(data: string) {
			// Up/down arrows
			if (data === "\x1b[A" || data === "\x1b[B") {
				index = index === 0 ? 1 : 0;
				refresh();
				return;
			}
			// Enter
			if (data === "\r" || data === "\n") {
				done(index === 0);
				return;
			}
			// Escape discards
			if (data === "\x1b") {
				done(false);
			}
		}

		function render(width: number): string[] {
			if (cachedLines) return cachedLines;
			const lines: string[] = [];
			const rule = "\u2500".repeat(width);
			lines.push(theme.fg("accent", rule));
			lines.push(theme.fg("text", " Record this architecture decision in the project decision log?"));
			lines.push("");
			lines.push(` ${theme.fg("accent", title)}`);
			lines.push(` ${theme.fg("muted", firstLine(decision, Math.max(20, width - 2)))}`);
			lines.push("");
			for (let i = 0; i < options.length; i++) {
				const selected = i === index;
				const prefix = selected ? theme.fg("accent", "> ") : "  ";
				const label = selected ? theme.fg("accent", options[i]) : theme.fg("text", options[i]);
				lines.push(prefix + label);
			}
			lines.push("");
			lines.push(theme.fg("dim", " \u2191\u2193 navigate \u2022 Enter to confirm \u2022 Esc to discard"));
			lines.push(theme.fg("accent", rule));
			cachedLines = lines;
			return lines;
		}

		return {
			render,
			invalidate: () => {
				cachedLines = undefined;
			},
			handleInput,
		};
	});

	return result === true;
}

export function createDecisionRecordToolDefinition(
	options: DecisionRecordToolOptions,
): ToolDefinition<typeof decisionRecordSchema, DecisionRecordToolDetails | undefined> {
	const ops = options.operations;

	return {
		name: "decision_record",
		label: "decision record",
		description:
			"Record an architecture or design decision in the project decision log (.elyra/memory/decisions/, committed to git and shared with the team). Use this ONLY for choices with lasting impact where alternatives were considered and rejected: framework or library selection, data model shape, API boundaries, infrastructure, conventions the whole codebase must follow. Do NOT use it for routine edits, bug fixes, or one-off implementation details. Recorded decisions are shown to future sessions so the same debate is not repeated.",
		promptSnippet: "Record an architecture decision in the project decision log",
		promptGuidelines: [
			"Only record decisions with lasting impact where alternatives were rejected. Avoid decision spam.",
			"Write context and alternatives so a future reader understands why, not just what.",
			"When a new decision reverses an earlier one, pass its id as 'supersedes' instead of recording a duplicate.",
		],
		parameters: decisionRecordSchema,
		async execute(_toolCallId, params, signal, _onUpdate, ctx) {
			const { title, context, decision, alternatives, consequences, supersedes } = params;
			const baseDetails: DecisionRecordToolDetails = { id: null, path: null, saved: false };

			if (!title?.trim()) throw new Error("Decision title is required.");
			if (!context?.trim()) throw new Error("Decision context is required.");
			if (!decision?.trim()) throw new Error("Decision text is required.");

			const mode = options.getDecisionLog();
			if (mode === "off") {
				return {
					content: [
						{
							type: "text",
							text: `Decision log is disabled (decisionLog: off). Decision "${title}" was not recorded.`,
						},
					],
					details: { ...baseDetails, disabled: true },
				};
			}

			const existing = listDecisions(options.cwd, ops);
			const similar = findSimilarDecision(existing, title);
			if (similar && !supersedes) {
				throw new Error(
					`A similar decision already exists: "${similar.title}" (${similar.id}). If this decision replaces it, pass supersedes: "${similar.id}". Otherwise choose a more specific title or skip recording.`,
				);
			}

			if (signal?.aborted) {
				throw new Error("Operation aborted.");
			}

			// Approval: skip when mode is auto or no UI is available.
			if (mode === "confirm") {
				const approved = await confirmRecord(ctx, title, decision);
				if (!approved) {
					return {
						content: [{ type: "text", text: `Decision "${title}" was not recorded (user declined).` }],
						details: { ...baseDetails, rejected: true },
					};
				}
			}

			if (signal?.aborted) {
				throw new Error("Operation aborted.");
			}

			const result = writeDecision(
				options.cwd,
				{
					title,
					context,
					decision,
					alternatives,
					consequences,
					supersedes,
					session: options.getSessionId?.(),
				},
				ops,
			);

			let text = `Recorded decision "${result.decision.title}" (${result.decision.id}) at ${result.path}.`;
			if (result.superseded) {
				text += ` Superseded ${result.superseded.id}.`;
			}

			return {
				content: [{ type: "text", text }],
				details: {
					...baseDetails,
					id: result.decision.id,
					path: result.path,
					saved: true,
					superseded: result.superseded?.id,
				},
			};
		},
		renderCall(args, theme, context) {
			const renderArgs = args as { title?: string; supersedes?: string } | undefined;
			const title = typeof renderArgs?.title === "string" ? renderArgs.title : "...";
			let text = `${theme.fg("toolTitle", theme.bold("decision record"))} ${theme.fg("accent", title)}`;
			if (typeof renderArgs?.supersedes === "string" && renderArgs.supersedes) {
				text += ` ${theme.fg("muted", `(supersedes ${renderArgs.supersedes})`)}`;
			}
			const component = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			component.setText(text);
			return component;
		},
		renderResult(result, _options, theme, context) {
			const details = result.details as DecisionRecordToolDetails | undefined;
			const text = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			if (details?.rejected) {
				text.setText(theme.fg("warning", "Discarded"));
			} else if (details?.disabled) {
				text.setText(theme.fg("muted", "Decision log off"));
			} else if (details?.saved) {
				const suffix = details.superseded ? ` \u2022 superseded ${details.superseded}` : "";
				text.setText(theme.fg("success", `\u2713 Recorded${suffix}`));
			} else if (context.isError) {
				const output = result.content
					.filter((c) => c.type === "text")
					.map((c) => ("text" in c ? c.text : ""))
					.join("\n");
				text.setText(theme.fg("error", output));
			} else {
				text.setText("");
			}
			return text;
		},
	};
}

export function createDecisionRecordTool(options: DecisionRecordToolOptions): AgentTool<typeof decisionRecordSchema> {
	return wrapToolDefinition(createDecisionRecordToolDefinition(options));
}
